import React, { Component } from 'react';
import axios from 'axios';

import { IconButton } from '@/components/ui/custom/IconButton';
import { PhoneOff } from 'lucide-react';
import { toast } from 'sonner';
import { getShowNewLayout } from '@/components/sidebar/layout-preference';
import { toastPosition } from '@/components/notifications/toastPosition';

/**
 * This component checks the Twilio phone verification status and renders a message.
 * If the phone number is local number, it checks the A2P campaign status.
 * If the phone number is a toll-free number, it checks the toll-free status.
 */
class PhoneVerificationChecker extends Component {
	state = {
		brandStatus: null, // A2P brand status
		campaignStatus: null, // A2P campaign status
		tollFreeStatus: null, // Toll-free status
		isTollFreeNumber: false, // Whether the phone number is toll-free
		byPass: false, // Whether to bypass A2P popup
		error: false,
		showNewLayout: getShowNewLayout(),
	};

	getComplianceSettingsUrl = () =>
		`${window.location.origin}/settings/compliance`;

	getLocalDayKey = () => {
		const d = new Date();
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${yyyy}-${mm}-${dd}`;
	};

	shouldShowToastToday = ({ title }) => {
		try {
			const key = `phone-verification-toast-last-shown:${title}`;
			const today = this.getLocalDayKey();
			const lastShownDay = localStorage.getItem(key);
			if (lastShownDay === today) return false;
			localStorage.setItem(key, today);
			return true;
		} catch {
			// If localStorage is unavailable, fall back to showing the toast.
			return true;
		}
	};

	getNotificationTitle = ({
		isTollFreeNumber = false,
		campaignStatus = '',
		brandStatus = '',
		tollFreeStatus = '',
		byPass = false,
	} = {}) => {
		// If bypass is enabled, don't show any notification
		if (byPass) {
			return null;
		}

		let tooltipTitle = '';

		if (!isTollFreeNumber) {
			if (
				(campaignStatus === '' && brandStatus === 'PENDING') ||
				(campaignStatus === '' && brandStatus === 'APPROVED')
			) {
				tooltipTitle = 'Phone verification in progress';
			} else if (campaignStatus === '') {
				tooltipTitle = 'Fill out A2P Registration';
			} else if (campaignStatus === 'IN_PROGRESS') {
				tooltipTitle = 'Phone verification in progress';
			} else if (campaignStatus === 'FAILED') {
				tooltipTitle = 'Phone verification failed. Contact support';
			}
		} else {
			if (
				tollFreeStatus === 'PENDING_REVIEW' ||
				tollFreeStatus === 'IN_REVIEW'
			) {
				tooltipTitle = 'Phone verification in progress';
			} else if (tollFreeStatus === 'TWILIO_REJECTED') {
				tooltipTitle = 'Phone verification rejected';
			}
		}

		if (!tooltipTitle) {
			return null;
		}

		return tooltipTitle;
	};

	async componentDidMount() {
		// if in dev mode, don't show the phone verification checker
		if (import.meta.env.DEV) {
			return null;
		}

		const data = await this.getPhoneStatus();

		if (this.state.showNewLayout && data) {
			// Don't show notification if bypass is enabled
			if (data.byPass) {
				console.log('bypass', data.byPass);
				return;
			}

			const title = this.getNotificationTitle(data);
			if (title && this.shouldShowToastToday({ title })) {
				// TODO: phone verification status needs to be improved
				toast.error(title, {
					action: {
						id: 'a2p-registration',
						label: title === 'Fill out A2P Registration' ? 'Go' : 'View',
						onClick: () => {
							window.location.replace(this.getComplianceSettingsUrl());
						},
					},
					position: toastPosition('top-left'),
					duration: Infinity,
				});
			}
		}
	}

	getPhoneStatus = async () => {
		try {
			const res = await axios.get('/api/twilio-phone-status');

			const {
				brandStatus,
				campaignStatus,
				tollFreeStatus,
				isTollFreeNumber,
				byPass,
				error,
			} = res.data;

			this.setState({
				brandStatus,
				campaignStatus,
				tollFreeStatus,
				isTollFreeNumber,
				byPass,
				error,
			});
			return res.data;
		} catch {
			this.setState({ error: true });
		}
	};

	render() {
		const {
			brandStatus,
			campaignStatus,
			tollFreeStatus,
			isTollFreeNumber,
			byPass,
			error,
		} = this.state;

		if (this.state.showNewLayout) {
			return null;
		}

		if (error) {
			return null;
		}

		// Don't render anything if bypass is enabled
		if (byPass) {
			return null;
		}

		let tooltipTitle = '';
		let onClick = () => {};

		if (!isTollFreeNumber) {
			if (
				(campaignStatus === '' && brandStatus === 'PENDING') ||
				(campaignStatus === '' && brandStatus === 'APPROVED')
			) {
				tooltipTitle = 'Phone verification in progress';
			} else if (campaignStatus === '') {
				tooltipTitle = 'Fill out A2P Registration';
			} else if (campaignStatus === 'IN_PROGRESS') {
				tooltipTitle = 'Phone verification in progress';
			} else if (campaignStatus === 'FAILED') {
				tooltipTitle = 'Phone verification failed. Contact support';
			}
		} else {
			if (
				tollFreeStatus === 'PENDING_REVIEW' ||
				tollFreeStatus === 'IN_REVIEW'
			) {
				tooltipTitle = 'Phone verification in progress';
			} else if (tollFreeStatus === 'TWILIO_REJECTED') {
				tooltipTitle = 'Phone verification rejected';
			}
		}

		if (!tooltipTitle) {
			return null;
		}

		onClick = () => {
			window.location.replace(this.getComplianceSettingsUrl());
		};

		return (
			<IconButton
				aria-label="phone off"
				toolTipTitle={tooltipTitle}
				onClick={onClick}
			>
				<PhoneOff
					className="size-4"
					color={
						tooltipTitle === 'Phone verification in progress'
							? undefined
							: '#E11D4B'
					}
				/>
			</IconButton>
		);
	}
}

export default PhoneVerificationChecker;
